import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/returns.scss";

function Returns() {
  const returns = siteConfig.returns || {};
  const returnEmail = returns.email || siteConfig.contact?.email;

  return (
    <main className="content-page policy-page returns-page">
      <header className="page-hero">
        <div className="page-shell page-hero__content">
          <p className="eyebrow">Returns &amp; refunds</p>
          <h1>We want every order to feel right.</h1>
          <p className="page-hero__lede">
            If something is not as expected, tell the {siteConfig.brandName}{" "}
            team and we will help find a fair solution.
          </p>
        </div>
      </header>

      <section className="page-section page-shell returns-intro">
        <div className="returns-intro__highlight">
          <span aria-hidden="true">100%</span>
          <p>{returns.guarantee || "satisfaction guarantee"}</p>
        </div>
        <div className="prose">
          <h2>Our return promise</h2>
          <p>
            If a product arrives damaged, incorrect, spoiled, or simply does not
            meet your expectations, contact us promptly with your order number.
            We will review the issue and arrange an appropriate replacement,
            credit, or refund.
          </p>
          <p>
            Because food safety matters, please do not mail food back unless our
            customer care team asks you to. Keep the product and shipping
            packaging until we have replied.
          </p>
          <p>
            This voluntary promise operates in addition to consumer guarantees
            and other rights that cannot lawfully be excluded.
          </p>
        </div>
      </section>

      <section className="page-section page-section--tinted">
        <div className="page-shell policy-layout">
          <nav className="policy-nav" aria-label="On this page">
            <p className="eyebrow">On this page</p>
            <a href="#start-return">Report an issue</a>
            <a href="#damaged-items">Damaged items</a>
            <a href="#return-shipping">Return shipping</a>
            <a href="#refunds">Refund timing</a>
            <a href="#change-of-mind">Change of mind</a>
            <a href="#consumer-rights">Consumer rights</a>
          </nav>

          <div className="prose policy-copy">
            <section id="start-return">
              <h2>How to start a return</h2>
              <ol>
                <li>
                  Contact us promptly with your order number and the item that
                  needs attention. Prompt contact helps us preserve carrier and
                  batch evidence but does not shorten a statutory claim period.
                </li>
                <li>
                  Share a short description and, for shipping damage, clear
                  photos of the product, box, and label.
                </li>
                <li>
                  Wait for the replacement, refund, or return instructions from
                  our customer care team before mailing anything.
                </li>
              </ol>
              <p>
                {returnEmail ? (
                  <a href={`mailto:${returnEmail}?subject=Return request`}>
                    Email {returnEmail} to begin
                  </a>
                ) : (
                  <Link to="/contact">Contact customer care to begin</Link>
                )}
                .
              </p>
            </section>

            <section id="damaged-items">
              <h2>Damaged, incorrect, or spoiled items</h2>
              <p>
                Report a delivery problem as soon as you notice it. Include your
                order number, a description of the issue, and photos of the
                product, shipping box, and label. Do not discard the item or
                packaging until we reply.
              </p>
              <p>
                When an item is confirmed damaged, defective, spoiled on
                arrival, or sent in error, we will offer an appropriate
                replacement or refund at no additional cost. Availability may
                determine which remedy is possible.
              </p>
            </section>

            <section id="return-shipping">
              <h2>Return shipping</h2>
              <p>
                If we ask you to return a confirmed damaged, defective, or
                incorrect item, {siteConfig.brandName} will provide the shipping
                instructions and cover the approved return cost. Terms for any
                other authorized return will be shared before you send it.
              </p>
            </section>

            <section id="refunds">
              <h2>Refunds and replacements</h2>
              <p>
                We inspect eligible returns after receipt. Approved refunds are
                sent to the original payment method, normally within 5–10
                business days of inspection. Your bank may need additional time
                to post the credit. A refund owed to the purchaser is returned to
                the original payment method unless the purchaser agrees to store
                credit or the law permits another method. Gift recipients can
                contact us to discuss an appropriate remedy without disclosing
                the gift price unnecessarily.
              </p>
              <p>
                Shipping questions are covered in our{" "}
                <Link to="/shipping">shipping and delivery policy</Link>.
              </p>
            </section>

            <section id="change-of-mind">
              <h2>Change-of-mind requests</h2>
              <p>
                Under our voluntary satisfaction promise, contact us promptly if
                a product is not right for you. For food-safety reasons, do not
                return pantry goods unless instructed. We will consider the item,
                condition, delivery date, and circumstances and explain any
                available refund, replacement, or credit before asking you to act.
              </p>
              <p>
                Customised goods, opened food, and goods that cannot safely be
                resold may not be eligible for a change-of-mind return. This
                exclusion does not apply when the product is faulty, unsafe,
                misdescribed, incorrectly supplied, or otherwise covered by law.
              </p>
            </section>

            <section id="consumer-rights">
              <h2>Consumer guarantees</h2>
              <p>
                Nothing in this policy excludes, restricts, or modifies a right
                or remedy that cannot lawfully be excluded. For purchases covered
                by the Australian Consumer Law, the remedy for a major problem may
                differ from the remedy for another failure. Read our{" "}
                <Link to="/consumer-guarantees">consumer guarantees policy</Link>
                {" "}for more information.
              </p>
              <p><Link to="/policies">View all store policies</Link>.</p>
            </section>
          </div>
        </div>
      </section>
    </main>
  );
}

export default Returns;
